import ApiClient, { ApiError } from "../../backends/ApiClient";
import { useEffect, useState } from "react";
import NotificationManager from "../../components/notifications/NotificationManager";
import { NotificationItem } from "./NotificationItem";
import styles from "../styles/userSettingsPageStyles.module.scss";
import ToggleSwitch from "../../components/admin/sidebar/items/ToggleSwitch";
import PhoneNumber, {
  formatPhoneNumber,
} from "../../components/utilities/PhoneNumber";
import useAsyncEffect from "../../hooks/useAsyncEffect";
import { observer } from "mobx-react";
import EditableField from "../../components/utilities/EditableField";
import ContactEmailField from "../../components/utilities/ContactEmailField";
import FontAwesome from "../../components/utilities/FontAwesome";
import useSWR from "swr";
import { blockObjectFactory } from "../../components/blocks/SchoolBlocks/blockObjectFactory";
import SchoolBlocksLoadingIndicator from "../../components/utilities/SchoolBlocksLoadingIndicator";
import { getDistrict } from "../../utils/SchoolBlocksUtilities";
import SendTestButton from "./SendTestButton";
import OrgSubscriptionsTable from "./OrgSubscriptionsTable";
import { IUserWithSubscriptions } from "./UserSettingsPage";
import ClickableLink from "../../components/utilities/ClickableLink";
import { useStores } from "../../stores/useStores";
import { userAgentFromString } from "next/server";
import { useFeatureFlags } from "../../utils/featureFlags/useFeatureFlags";
import { reportError } from "../../utils/errorReporting";

export interface IPersonBlockWithPushSubscriptions extends Omit<
  IPersonBlock,
  "blockModel"
> {
  blockModel: IUserWithSubscriptions;
}

export function askPermission() {
  return new Promise(function (resolve, reject) {
    const permissionResult = Notification.requestPermission(function (result) {
      resolve(result);
    });

    if (permissionResult) {
      permissionResult.then(resolve, reject);
    }
  }).then(function (permissionResult) {
    if (permissionResult !== "granted") {
      throw new Error(
        "Permission not granted to enable push notifications! Please try again.",
      );
    }
  });
}

export function isPushSupported() {
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function getDeviceStringFromUserAgent(
  parsedUserAgent: SoprisAppsProps["pageProps"]["parsedUserAgent"],
) {
  let deviceModel = parsedUserAgent.device.model;
  if (!deviceModel) {
    // ua-parser doesn't handle desktop devices, this is the best guess
    // see https://github.com/faisalman/ua-parser-js/issues/182
    deviceModel = `${parsedUserAgent.os.name} Desktop`;
  }
  return `${deviceModel || "Unknown Device"}/${parsedUserAgent.browser.name}`;
}

export const NotificationSettings = observer((props) => {
  const { organizationStore, interfaceStore, userStore } = useStores();
  const { voice_notifications_enabled } = useFeatureFlags();

  const [pushSupported, setPushSupported] = useState(false);
  const [orgList, setOrgList] = useState<OrganizationTypeOrganization[]>([]);
  const deviceType = interfaceStore.parsedUserAgent.device.type || "desktop";
  const [testNotificationIds, setTestNotificationIds] = useState({
    Email: "",
    SMS: "",
    Push: "",
  });
  const helpDocUrl =
    "https://support.schoolblocks.com/article/99-notification-troubleshooting-guide";
  const [editingField, setEditingField] = useState<string | null>(null);

  const { data, isLoading } = useSWR<any, IPersonBlockWithPushSubscriptions>(
    "fetch-user-obj-with-subscriptions",
    async () => {
      try {
        const client = await ApiClient();
        const blockModel = await (client.GET(
          "/organizations/{organization_pk}/users/{id}/",
          {
            params: {
              path: {
                organization_pk: organizationStore.currentOrganization.id,
                id: userStore.id,
              },
              // the spec types `expand` as a scalar string (SBSB-2912)
              query: { expand: ["push_subscriptions.organization"].join(",") },
            },
          },
        ) as any);
        return blockObjectFactory("person", {
          blockModel,
        } as IPersonBlock);
      } catch (e) {
        console.error(e);
        reportError(e, { error_source: "handled" });
        NotificationManager.error(
          "Error fetching user information. Please try again.",
          "",
          8000,
        );
        return {
          blockModel: {},
        };
      }
    },
    {
      revalidateOnMount: true,
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  const getAllOrgs = async () => {
    try {
      const district = getDistrict(organizationStore.currentOrganization);
      const client = await ApiClient();
      const result = await client.GET("/organizations/", {
        params: {
          query: {
            path__descendant_or_eq: district.path,
            // the spec types `ordering` as a scalar string (SBSB-2912)
            ordering: ["path", "-title"].join(","),
            // the spec types `deleted` as a boolean, but the backend expects
            // the string "false" here (SBSB-2912)
            deleted: "false" as unknown as boolean,
            type__in: ["school", "district"],
            // the spec types `expand` as a scalar string (SBSB-2912)
            expand: ["is_following"].join(","),
            page_size: 1000,
          },
        },
      });
      setOrgList(result.data as unknown as OrganizationTypeOrganization[]);
    } catch (error) {
      reportError(error, { error_source: "handled" });
    }
  };

  const emailEnabled = !!(
    data?.blockModel.notifications?.email && data.blockModel.email
  );
  const phoneEnabled = !!(
    data?.blockModel.notifications?.phone && data.blockModel.mobile_phone
  );
  const voiceEnabled = !!(
    data?.blockModel.notifications?.voice && data.blockModel.mobile_phone
  );
  const pushEnabled = !!data?.blockModel.notifications?.push;
  const pushSubscriptions = data?.blockModel.push_subscriptions;

  const handlePhoneSave = async (phoneValue: string) => {
    const phoneNumber = Number(phoneValue?.replace(/\D/g, ""));
    try {
      const client = await ApiClient();
      await client.PATCH("/organizations/{organization_pk}/users/{id}/", {
        params: {
          path: {
            organization_pk: organizationStore.currentOrganization.id,
            id: data?.blockModel.id,
          },
        },
        // the spec's UserRequest requires `username`, but this is a partial
        // update and the backend has always accepted a subset of fields (SBSB-2912)
        body: { mobile_phone: phoneNumber } as never,
      });
      data &&
        data.setAttributes({
          blockModel: {
            ...data.blockModel,
            mobile_phone: phoneNumber,
          },
        });
      NotificationManager.success("Phone number updated successfully.");
      setEditingField(null);
    } catch (e) {
      reportError(e, { error_source: "handled" });
      NotificationManager.error("Error updating phone number.");
    }
  };

  useAsyncEffect(async () => {
    if (pushSubscriptions?.length === 0 && pushEnabled) {
      // Turn off push notifications if there are no subscriptions
      await updateNotificationSetting("push");
    } else if (!pushEnabled && pushSubscriptions?.length) {
      // Turn on push notifications if there are active subscriptions
      await updateNotificationSetting("push");
    }
  }, [pushSubscriptions?.length, pushEnabled]);

  useEffect(() => {
    if (
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window
    ) {
      void navigator.serviceWorker.ready.then((serviceWorker) => {
        if (serviceWorker.active) {
          setPushSupported(true);
        }
      });
    }
    void getAllOrgs();
  }, []);

  const updateNotificationSetting = async (
    field: "push" | "phone" | "email" | "voice",
  ) => {
    try {
      const stateMapping = {
        phone: phoneEnabled,
        push: pushEnabled,
        email: emailEnabled,
        voice: voiceEnabled,
      };

      const client = await ApiClient();
      const userObj = await (client.PATCH(
        "/organizations/{organization_pk}/users/{id}/",
        {
          params: {
            path: {
              organization_pk: organizationStore.currentOrganization.id,
              id: data?.blockModel.id,
            },
          },
          // the spec's UserRequest requires `username`, but this is a partial
          // update and the backend has always accepted a subset of fields (SBSB-2912)
          body: {
            notifications: {
              phone: phoneEnabled,
              email: emailEnabled,
              push: pushEnabled,
              voice: voiceEnabled,
              [field]: !stateMapping[field],
            },
          } as never,
        },
      ) as any);
      data &&
        data.setAttributes({
          blockModel: {
            ...data.blockModel,
            notifications: userObj.notifications,
          },
        });
      NotificationManager.success("Settings have been updated!");
    } catch (e) {
      reportError(e, { error_source: "handled" });
      throw new Error("Something went wrong! Please try again.");
    }
  };

  async function createPushSubscription() {
    try {
      const registration = await navigator.serviceWorker.ready;
      await askPermission();
      const pushSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey:
          "BFcZZ6FiITumPysCi_t9B1TANBHATH7r0Y4bKVRomTVRo14k5eNU0L9LhgZ3ogL7xPkGYh-RqtFN14RlnU3rDWg",
      });
      const client = await ApiClient();
      const newSubscription = await (client.POST(
        "/users/{user_pk}/push_subscription/",
        {
          params: { path: { user_pk: userStore.id } },
          body: {
            organization_id: organizationStore.organization.id,
            user_id: userStore.id,
            subscription_object: pushSubscription,
            user_agent: interfaceStore.userAgent,
            device_type: deviceType,
          } as never,
        },
      ) as any);
      data &&
        data.setAttributes({
          blockModel: {
            ...data.blockModel,
            push_subscriptions: [
              ...data.blockModel.push_subscriptions.filter(
                (sub) => sub.id !== newSubscription.id,
              ),
              {
                ...newSubscription,
                organization: organizationStore.organization,
              },
            ],
          },
        });
    } catch (subscriptionError) {
      if (!/Permission not granted/.test(subscriptionError.message)) {
        reportError(subscriptionError, { error_source: "handled" });
      }
      throw new Error(subscriptionError.message);
    }
  }

  async function deletePushSubscription(push_subscription) {
    const client = await ApiClient();
    await client.DELETE("/users/{user_pk}/push_subscription/{id}/", {
      params: {
        path: { user_pk: userStore.id, id: push_subscription.id },
      },
    });
    data &&
      data.setAttributes({
        blockModel: {
          ...data.blockModel,
          push_subscriptions: data.blockModel.push_subscriptions.filter(
            (sub) => sub.id !== push_subscription.id,
          ),
        },
      });
  }

  async function sendTestNotification(channel, device) {
    const userId = userStore.id;
    try {
      const client = await ApiClient();
      const result = await (client.GET(
        "/organizations/{organization_pk}/users/{id}/send_test_notification/",
        {
          params: {
            path: {
              organization_pk: organizationStore.organization.id,
              id: userId,
            },
            query: { channel: channel.toLowerCase(), device: device },
          },
        },
      ) as any);
      NotificationManager.success("Test notification sent!");
      setTestNotificationIds({
        ...testNotificationIds,
        // the spec types the response as a User, but at runtime it includes
        // a `test_notification_id` field (SBSB-2912)
        [channel]: result.test_notification_id,
      });
    } catch (error) {
      reportError(error, { error_source: "handled" });
    }
  }

  const subscriptionForCurrentDeviceAndOrganization = pushSubscriptions?.find(
    (sub) => {
      return (
        getDeviceStringFromUserAgent(userAgentFromString(sub.user_agent)) ===
          getDeviceStringFromUserAgent(interfaceStore.parsedUserAgent) &&
        sub.organization_id === organizationStore.organization.id
      );
    },
  );

  const handleToggle = async (orgId) => {
    try {
      const client = await ApiClient();
      await client.GET(
        "/toggle_follow/{itemname}/toggle_follow_organization/",
        {
          params: { path: { itemname: orgId } },
        },
      );

      // Update the organization in the state immutably
      setOrgList((prevOrgList) =>
        prevOrgList.map((org) =>
          org.id === orgId ? { ...org, is_following: !org.is_following } : org,
        ),
      );
      if (orgId === organizationStore.currentOrganization.id) {
        const newValue = !userStore.follower;
        userStore.setPermissions({
          follower: newValue,
        });
      }

      const toggledOrg = orgList.find((org) => org.id === orgId);
      if (toggledOrg) {
        NotificationManager.success(
          toggledOrg?.is_following
            ? `You are no longer subscribed to ${toggledOrg.title}.`
            : `You are now subscribed to ${toggledOrg.title}.`,
        );
      }
    } catch (e) {
      reportError(e, { error_source: "handled" });
      NotificationManager.error(e instanceof ApiError ? e.data : e.message);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.notificationSettings}>
        <SchoolBlocksLoadingIndicator fill={"$dark-gray"} size={70} />
      </div>
    );
  }

  return (
    <div id="my-notification-settings-tab">
      <h3 className={styles.heading}>Notification Settings</h3>
      <p>
        Please select which channels you want to receive notifications through.
        All enabled channels will be used when an admin sends a notification.
      </p>
      <p>
        If you think you should be receiving a notification, but are not, please
        check out our
        <ClickableLink target={"_blank"} href={helpDocUrl}>
          {" "}
          help document.
        </ClickableLink>
      </p>

      {/* Table layout with horizontal row dividers only */}
      <table className={styles.settingsTable}>
        <thead>
          <tr>
            <th>Notification</th>
            <th>Enabled</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {/* Phone (Text Messages) Row */}
          <tr>
            <td>
              <div className={styles.notificationInfo}>
                <FontAwesome
                  ariaHidden={true}
                  prefix={"fas"}
                  name="fa-comment"
                  className={styles.itemIcon}
                />
                <div className={styles.notificationDetails}>
                  <EditableField
                    key="mobile_phone"
                    defaultValue={
                      data && data.blockModel.mobile_phone
                        ? formatPhoneNumber(data.blockModel.mobile_phone)
                            .formattedNumber
                        : ""
                    }
                    isEditing={editingField === "mobile_phone"}
                    onEditStart={() => setEditingField("mobile_phone")}
                    onSave={handlePhoneSave}
                    onCancel={() => setEditingField(null)}
                    canEdit={true}
                    placeholder="Enter phone..."
                    className={styles.editable}
                  >
                    {data && data.blockModel.mobile_phone ? (
                      <PhoneNumber number={data.blockModel.mobile_phone} />
                    ) : (
                      <span>Enter Phone</span>
                    )}
                  </EditableField>
                  <small>Text Messages</small>
                </div>
              </div>
            </td>
            <td>
              <ToggleSwitch
                onClick={() => updateNotificationSetting("phone")}
                on={phoneEnabled}
                enabled={!!(data && data.blockModel.mobile_phone)}
                title={
                  data && data.blockModel.mobile_phone
                    ? "Toggle text messages"
                    : "Add a phone number to enable"
                }
              />
            </td>
            {phoneEnabled && (
              <td>
                <SendTestButton
                  channel="SMS"
                  sendTestNotification={sendTestNotification}
                  className={styles.sendTestNotification}
                />
              </td>
            )}
          </tr>

          {/* Voice Notifications Row */}
          {voice_notifications_enabled && (
            <tr>
              <td>
                <div className={styles.notificationInfo}>
                  <FontAwesome
                    ariaHidden={true}
                    prefix={"fas"}
                    name="fa-phone"
                    className={styles.itemIcon}
                  />

                  <div className={styles.notificationDetails}>
                    <EditableField
                      defaultValue={
                        data && data.blockModel.mobile_phone
                          ? formatPhoneNumber(data.blockModel.mobile_phone)
                              .formattedNumber
                          : ""
                      }
                      isEditing={editingField === "phone"}
                      onEditStart={() => setEditingField("phone")}
                      onSave={handlePhoneSave}
                      onCancel={() => setEditingField(null)}
                      canEdit={true}
                      placeholder="Enter phone..."
                      className={styles.editable}
                    >
                      {data && data.blockModel.mobile_phone ? (
                        <PhoneNumber number={data.blockModel.mobile_phone} />
                      ) : (
                        <span>Enter Phone</span>
                      )}
                    </EditableField>
                    <small>Voice Messages</small>
                  </div>
                </div>
              </td>
              <td>
                <ToggleSwitch
                  onClick={() => updateNotificationSetting("voice")}
                  on={voiceEnabled}
                  enabled={!!(data && data.blockModel.mobile_phone)}
                  title={
                    data && data.blockModel.mobile_phone
                      ? "Toggle voice messages"
                      : "Add a phone number to enable"
                  }
                />
              </td>
            </tr>
          )}

          {/* Email Notifications Row */}
          <tr>
            <td>
              <div className={styles.notificationInfo}>
                <FontAwesome
                  ariaHidden={true}
                  prefix={"fas"}
                  name="fa-at"
                  className={styles.itemIcon}
                />
                <div className={styles.notificationDetails}>
                  <ContactEmailField
                    userId={userStore.id}
                    email={data?.blockModel.email || ""}
                    className={styles.editable}
                  />
                  <small>Email Notifications</small>
                </div>
              </div>
            </td>
            <td>
              <ToggleSwitch
                onClick={() => updateNotificationSetting("email")}
                on={emailEnabled}
                enabled={!!(data && data.blockModel.email)}
                title={
                  data && data.blockModel.email
                    ? "Toggle email messages"
                    : "Add an email to enable"
                }
              />
            </td>
            {emailEnabled && (
              <td>
                <SendTestButton
                  channel="Email"
                  sendTestNotification={sendTestNotification}
                  className={styles.sendTestNotification}
                />
              </td>
            )}
          </tr>

          {/* Push Notifications (for current device) Row */}
          <tr>
            <td>
              <div className={styles.notificationInfo}>
                <FontAwesome
                  ariaHidden={true}
                  prefix={"fas"}
                  name={interfaceStore.isPWA ? "fa-mobile-alt" : "fa-laptop"}
                />
                <div className={styles.notificationDetails}>
                  <p>{organizationStore.organization.title}</p>
                  <small>
                    {getDeviceStringFromUserAgent(
                      interfaceStore.parsedUserAgent,
                    )}{" "}
                    Notifications
                  </small>
                </div>
              </div>
            </td>
            <td>
              <ToggleSwitch
                enabled={pushSupported}
                title={
                  pushSupported
                    ? "Enable app notifications on current device"
                    : "Push notifications are not supported on this device or browser"
                }
                onClick={async () => {
                  if (subscriptionForCurrentDeviceAndOrganization) {
                    await deletePushSubscription(
                      subscriptionForCurrentDeviceAndOrganization,
                    );
                  } else {
                    try {
                      await createPushSubscription();
                    } catch (e) {
                      NotificationManager.error(e.message);
                    }
                  }
                }}
                on={!!subscriptionForCurrentDeviceAndOrganization}
              />
            </td>
            {!!subscriptionForCurrentDeviceAndOrganization && (
              <td>
                <SendTestButton
                  channel="Push"
                  device={interfaceStore.userAgent}
                  sendTestNotification={sendTestNotification}
                  className={styles.sendTestNotification}
                />
              </td>
            )}
          </tr>

          {/* Extra push subscriptions (if any) */}
          {pushSubscriptions
            ?.filter((sub) =>
              subscriptionForCurrentDeviceAndOrganization
                ? sub.id !== subscriptionForCurrentDeviceAndOrganization.id
                : true,
            )
            .map((sub) => (
              <NotificationItem
                key={sub.id}
                push_subscription={sub}
                deletePushSubscription={deletePushSubscription}
                sendTestNotification={sendTestNotification}
              />
            ))}
          {pushSubscriptions &&
            !pushSubscriptions.some((sub) => sub.device_type === "mobile") && (
              <tr>
                <td>
                  <div className={styles.notificationInfo}>
                    <FontAwesome
                      ariaHidden={true}
                      prefix="fas"
                      name="fa-mobile-alt"
                      className={styles.itemIcon}
                    />
                    <div className={styles.notificationDetails}>
                      <p>Mobile App</p>
                      <small>
                        Visit the website on mobile to download the app!
                      </small>
                    </div>
                  </div>
                </td>
                <td></td>
                <td></td>
              </tr>
            )}
        </tbody>
      </table>

      <OrgSubscriptionsTable
        orgList={orgList}
        onToggleOrg={handleToggle}
        setActiveTab={props.setCurrentView}
      />
    </div>
  );
});

export default NotificationSettings;
